import "./styles.css";
import { api, getToken } from "./api/client";
import { consumeBridgeToken, loadMe, logout } from "./auth/session";
import { bootEngine } from "./engine/opendaw";
import type { StudioUser } from "./api/client";

const app = document.querySelector<HTMLDivElement>("#app")!;

async function start() {
  const params = new URLSearchParams(location.search);
  const bridge = params.get("token") || params.get("sso");
  if (bridge) {
    try {
      await consumeBridgeToken(bridge);
      history.replaceState({}, "", "/");
    } catch (err) {
      renderGate(`SSO failed: ${(err as Error).message}`);
      return;
    }
  }

  if (!getToken()) {
    renderGate("Open Studio from SoundSwift to continue. Your session is issued as a short-lived signed token — no second password.");
    return;
  }

  try {
    const user = await loadMe();
    await renderDashboard(user);
  } catch {
    renderGate("Your Studio session expired. Return to SoundSwift and choose Open Studio.");
  }
}

function renderGate(message: string) {
  app.innerHTML = `
    <div class="login">
      <h1>STUDIO</h1>
      <p>${escapeHtml(message)}</p>
      <p>This app lives at studio.soundswift.com and never writes into DeepSound core files.</p>
      <a class="primary" href="https://www.soundswift.com">Back to SoundSwift</a>
    </div>`;
}

async function renderDashboard(user: StudioUser) {
  app.innerHTML = `
    <aside class="sidebar">
      <div class="brand">SS STUDIO</div>
      <button class="nav-btn active" data-view="session">Session</button>
      <button class="nav-btn" data-view="library">Stems</button>
      <button class="nav-btn" data-view="projects">Projects</button>
      <div style="flex:1"></div>
      <button class="ghost" id="logout">Sign out</button>
    </aside>
    <section class="main">
      <header class="topbar">
        <strong>Multitrack session</strong>
        <div class="user">${escapeHtml(user.name || user.username)}</div>
      </header>
      <div class="workspace">
        <aside class="rail">
          <div class="panel">
            <h2>Projects</h2>
            <div id="project-list" class="status">Loading…</div>
            <button class="primary" id="new-project">New project</button>
          </div>
        </aside>
        <div class="canvas">
          <div class="panel">
            <h2>OpenDAW timeline</h2>
            <div id="timeline" class="timeline"></div>
          </div>
          <div class="panel">
            <h2>Authorized SoundSwift stems</h2>
            <div id="stems" class="status">Loading library…</div>
          </div>
        </div>
      </div>
    </section>`;

  document.getElementById("logout")?.addEventListener("click", async () => {
    await logout();
    location.reload();
  });
  document.getElementById("new-project")?.addEventListener("click", createProject);

  await bootEngine(document.getElementById("timeline")!);
  await refreshProjects();
  await refreshStems();
}

async function createProject() {
  await api("projects", {
    method: "POST",
    body: JSON.stringify({ title: "Session " + new Date().toLocaleString() }),
  });
  await refreshProjects();
}

async function refreshProjects() {
  const el = document.getElementById("project-list");
  if (!el) return;
  try {
    const data = await api<{ projects: Array<{ id: number; title: string }> }>("projects");
    if (!data.projects.length) {
      el.textContent = "No projects yet.";
      return;
    }
    el.innerHTML = data.projects
      .map((p) => `<div class="list-item" data-id="${p.id}">${escapeHtml(p.title)}</div>`)
      .join("");
  } catch (err) {
    el.textContent = (err as Error).message;
  }
}

async function refreshStems() {
  const el = document.getElementById("stems");
  if (!el) return;
  try {
    const data = await api<{ stems: Array<{ title: string; audio_id: string }> }>("soundsSwift/stems");
    if (!data.stems.length) {
      el.textContent = "No authorized stems found on this SoundSwift account.";
      return;
    }
    el.innerHTML = data.stems
      .map((s) => `<div class="list-item">${escapeHtml(s.title || s.audio_id)}</div>`)
      .join("");
  } catch (err) {
    el.textContent = (err as Error).message;
  }
}

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]!));
}

void start();
